import { useState, useEffect, useCallback, useRef } from 'react';

const MEDIA_API = (process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001') + '/api/media';

function formatSize(b) {
  if (!b) return '';
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

// ── Upload tab ────────────────────────────────────────────
function UploadTab({ accept, onPick }) {
  const inputRef           = useRef();
  const [uploading, setUploading]   = useState(false);
  const [progress, setProgress]     = useState(0);
  const [error, setError]           = useState('');
  const [dragging, setDragging]     = useState(false);
  const [preview, setPreview]       = useState(null);

  const doUpload = async (file) => {
    if (!file) return;
    setError(''); setUploading(true); setProgress(0);

    // Show local preview immediately
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = e => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }

    // Fake progress ticker
    const ticker = setInterval(() => {
      setProgress(p => p < 85 ? p + 10 : p);
    }, 150);

    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('alt_text', file.name.replace(/\.[^.]+$/, ''));

      const res = await fetch(`${MEDIA_API}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('mh_token')}` },
        body: fd,
      });
      clearInterval(ticker);
      setProgress(100);

      const data = await res.json();
      if (!res.ok || !data.data) {
        setError(data.error || 'Upload failed');
        setUploading(false);
        return;
      }
      // Short pause to show 100% then pick
      setTimeout(() => {
        setUploading(false);
        onPick(data.data.file_url);
      }, 400);
    } catch (err) {
      clearInterval(ticker);
      setError(err.message || 'Upload failed');
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) doUpload(file);
  };

  const acceptAttr = accept === 'image' ? 'image/*'
    : accept === 'video' ? 'video/*'
    : accept === 'document' ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv'
    : 'image/*,video/*,.pdf,.doc,.docx';

  return (
    <div style={{ padding:'16px 18px', display:'flex', flexDirection:'column', gap:14 }}>

      {/* Drop zone */}
      <div
        onDragOver={e=>{e.preventDefault();setDragging(true);}}
        onDragLeave={()=>setDragging(false)}
        onDrop={onDrop}
        onClick={()=>!uploading && inputRef.current?.click()}
        style={{
          border:`2px dashed ${dragging?'var(--accent)':'var(--border)'}`,
          borderRadius:'var(--radius-md)', padding:'32px 20px',
          textAlign:'center', cursor:uploading?'default':'pointer',
          background:dragging?'var(--accent-muted)':'var(--bg-elevated)',
          transition:'all 0.15s', minHeight:120,
          display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8,
        }}>
        {uploading ? (
          <>
            {preview && (
              <img src={preview} alt="" style={{ width:64, height:64, objectFit:'cover', borderRadius:8, marginBottom:4, opacity:0.7 }}/>
            )}
            <div style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:6 }}>Uploading…</div>
            <div style={{ width:180, height:4, background:'var(--border)', borderRadius:2, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${progress}%`, background:'var(--accent)', transition:'width 0.2s', borderRadius:2 }}/>
            </div>
            <div style={{ fontSize:11, color:'var(--text-muted)' }}>{progress}%</div>
          </>
        ) : (
          <>
            <span style={{ fontSize:28, opacity:0.35, color:'var(--accent)' }}>⬆</span>
            <div style={{ fontSize:13, fontWeight:500, color:'var(--text-secondary)' }}>Drop file here or click to browse</div>
            <div style={{ fontSize:11, color:'var(--text-muted)' }}>
              {accept==='image'?'Images (JPG, PNG, WebP, SVG)':accept==='video'?'Videos (MP4, WebM)':'Images · Videos · Documents'}
            </div>
          </>
        )}
      </div>

      {error && (
        <div style={{ padding:'8px 12px', background:'rgba(240,96,96,0.1)', border:'1px solid rgba(240,96,96,0.3)', borderRadius:6, fontSize:12, color:'#ef4444' }}>
          ⚠ {error}
        </div>
      )}

      <input ref={inputRef} type="file" accept={acceptAttr} style={{ display:'none' }}
        onChange={e=>e.target.files[0] && doUpload(e.target.files[0])}/>

      <div style={{ fontSize:11, color:'var(--text-muted)', textAlign:'center', lineHeight:1.6 }}>
        Uploaded files are saved to the Media Library and available for reuse.
      </div>
    </div>
  );
}

// ── Library tab ───────────────────────────────────────────
function LibraryTab({ accept, onPick }) {
  const [assets, setAssets]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [pagination, setPag]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit:20, page });
      if (accept !== 'all') params.set('type', accept);
      if (search) params.set('search', search);
      const res = await fetch(`${MEDIA_API}?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('mh_token')}` },
      });
      const data = await res.json();
      setAssets(data.data || []); setPag(data.pagination);
    } catch { setAssets([]); } finally { setLoading(false); }
  }, [accept, search, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1, minHeight:0 }}>
      {/* Search */}
      <div style={{ padding:'10px 18px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
        <input placeholder="Search files…" value={search}
          onChange={e=>{setSearch(e.target.value);setPage(1);}}
          style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'7px 12px', fontSize:12, outline:'none', boxSizing:'border-box' }}/>
      </div>

      {/* Grid */}
      <div style={{ flex:1, overflowY:'auto', padding:14 }}>
        {loading ? (
          <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>Loading…</div>
        ) : assets.length===0 ? (
          <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>
            <div style={{ fontSize:24, marginBottom:8, opacity:0.3 }}>◈</div>
            <div style={{ fontSize:12 }}>No files found. Switch to Upload tab to add files.</div>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))', gap:8 }}>
            {assets.map(a => {
              const thumb = a.thumbnail_url || (a.type==='image' ? a.file_url : null);
              return (
                <div key={a.id} onClick={()=>onPick(a.file_url)}
                  style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-md)', overflow:'hidden', cursor:'pointer', background:'var(--bg-elevated)', transition:'all 0.15s' }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.transform='translateY(-1px)';}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.transform='none';}}>
                  <div style={{ height:75, background:'var(--bg-surface)', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
                    {thumb
                      ? <img src={thumb} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} loading="lazy"/>
                      : <span style={{ fontSize:20, opacity:0.3 }}>◈</span>}
                  </div>
                  <div style={{ padding:'5px 7px' }}>
                    <div style={{ fontSize:10, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'var(--text-primary)' }}>{a.alt_text||a.filename}</div>
                    <div style={{ fontSize:10, color:'var(--text-muted)' }}>{formatSize(a.size_bytes)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination?.pages > 1 && (
        <div style={{ padding:'10px 14px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'center', gap:4, flexShrink:0 }}>
          {[...Array(Math.min(pagination.pages, 6))].map((_,i) => {
            const p = i+1;
            return (
              <button key={p} onClick={()=>setPage(p)} style={{ width:26, height:26, borderRadius:4, fontSize:11, cursor:'pointer', background:p===page?'var(--accent)':'var(--bg-elevated)', color:p===page?'#0d0f12':'var(--text-secondary)', border:`1px solid ${p===page?'var(--accent)':'var(--border)'}` }}>{p}</button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Combined Modal ────────────────────────────────────────
function MediaModal({ accept, onPick, onClose }) {
  const [tab, setTab] = useState('upload'); // 'upload' | 'library'

  const tabBtn = (t, label) => ({
    flex:1, padding:'10px', fontSize:12, fontWeight:tab===t?600:400,
    cursor:'pointer', background:'none', border:'none',
    borderBottom:`2px solid ${tab===t?'var(--accent)':'transparent'}`,
    color:tab===t?'var(--accent)':'var(--text-muted)',
    transition:'all 0.15s',
  });

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:3000, background:'rgba(0,0,0,0.8)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', width:660, maxWidth:'95vw', height:520, display:'flex', flexDirection:'column', animation:'fadeIn 0.2s ease' }}>

        {/* Header */}
        <div style={{ padding:'14px 18px 0', borderBottom:'1px solid var(--border)', display:'flex', flexDirection:'column', flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <span style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:13 }}>
              {accept==='image'?'Pick Image':accept==='video'?'Pick Video':'Pick File'}
            </span>
            <button onClick={onClose} style={{ color:'var(--text-muted)', background:'none', border:'none', cursor:'pointer', fontSize:16, lineHeight:1, padding:4 }}>✕</button>
          </div>
          {/* Tabs */}
          <div style={{ display:'flex', marginBottom:-1 }}>
            <button style={tabBtn('upload','upload')} onClick={()=>setTab('upload')}>⬆ Upload New</button>
            <button style={tabBtn('library','library')} onClick={()=>setTab('library')}>◈ From Library</button>
          </div>
        </div>

        {/* Tab content */}
        <div style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          {tab==='upload'
            ? <UploadTab accept={accept} onPick={url=>{onPick(url);onClose();}}/>
            : <LibraryTab accept={accept} onPick={url=>{onPick(url);onClose();}}/>
          }
        </div>
      </div>
    </div>
  );
}

// ── Field component (drop-in replacement) ─────────────────
export default function ImagePickerField({ label='Cover Image', value, onChange, accept='image' }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <div style={{ fontSize:12, color:'var(--text-secondary)', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.03em', marginBottom:8 }}>{label}</div>

      {value ? (
        <div style={{ position:'relative', borderRadius:'var(--radius-md)', overflow:'hidden', border:'1px solid var(--border)' }}>
          <img src={value} alt="" style={{ width:'100%', height:140, objectFit:'cover', display:'block' }} onError={e=>{e.target.style.display='none';}}/>
          <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)', padding:'24px 10px 8px', display:'flex', gap:6, justifyContent:'flex-end' }}>
            <button onClick={()=>setOpen(true)} style={{ padding:'4px 10px', borderRadius:4, background:'var(--accent)', color:'#0d0f12', border:'none', fontSize:11, fontWeight:600, cursor:'pointer' }}>Change</button>
            <button onClick={()=>onChange('')} style={{ padding:'4px 10px', borderRadius:4, background:'rgba(240,96,96,0.85)', color:'#fff', border:'none', fontSize:11, cursor:'pointer' }}>Remove</button>
          </div>
        </div>
      ) : (
        <div
          onClick={()=>setOpen(true)}
          style={{ height:90, border:'2px dashed var(--border)', borderRadius:'var(--radius-md)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6, cursor:'pointer', background:'var(--bg-elevated)', transition:'all 0.15s' }}
          onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.background='var(--accent-muted)';}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='var(--bg-elevated)';}}>
          <span style={{ fontSize:22, opacity:0.4, color:'var(--accent)' }}>◈</span>
          <span style={{ fontSize:11, color:'var(--text-muted)' }}>Upload or pick from library</span>
        </div>
      )}

      {open && (
        <MediaModal accept={accept} onPick={url=>{onChange(url);setOpen(false);}} onClose={()=>setOpen(false)}/>
      )}
    </div>
  );
}